import { useMemo } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useFormContext } from 'react-hook-form';
import { DialogProps } from '@radix-ui/react-dialog';

import { useSettingsStore } from '@/store/use-settings-store';
import { COLLATERAL_SYMBOL, useMarketStore } from '@/store/use-markets-store';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { Label } from '@/components/ui/label';
import { formatNumber } from '@/utils/value-format';
import { abbrFromSym } from '@/utils/token-symbol';

import { OrderFormSchema } from './order-form';

interface ScaleOrderConfirmProps extends DialogProps {
  scaleOrders: OrderFormSchema[];
  onSubmit: () => void;
}

const ScaleOrderConfirm = (props: ScaleOrderConfirmProps) => {
  const { open, scaleOrders, onSubmit, onOpenChange } = props;

  const form = useFormContext<OrderFormSchema>();
  const symbol = form.watch('order.symbol');
  const isBuy = form.watch('order.isBuy');

  const baseSymbol = abbrFromSym(symbol);

  const { sizeDecimals, priceDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      sizeDecimals: state.marketSpec.sizeDecimals,
      priceDecimals: state.marketSpec.priceDecimals,
    })),
  );

  const size = useMemo(
    () => scaleOrders.reduce((prev, curr) => prev + +curr.order.size, 0),
    [scaleOrders],
  );

  const { isSubmitting } = form.formState;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hideCloseButton className="sm:max-w-md">
        <DialogHeader className="p-5 pb-0">
          <DialogTitle className="text-xl text-foreground">
            Confirm Order
          </DialogTitle>
        </DialogHeader>

        <div className="px-5">
          <div className="flex h-10 items-center justify-between">
            <p className="flex items-center gap-2">Action</p>

            <span
              className={`font-mono leading-tight ${isBuy ? 'text-green' : 'text-red'}`}
            >
              {isBuy ? 'Buy' : 'Sell'}
            </span>
          </div>

          <div className="flex h-10 items-center justify-between">
            <p className="flex items-center gap-2">Size</p>

            <span
              className={`font-mono leading-tight ${isBuy ? 'text-green' : 'text-red'}`}
            >
              {formatNumber(size, {
                digits: Number(sizeDecimals),
              })}{' '}
              {baseSymbol}
            </span>
          </div>

          <div className="flex h-10 items-center justify-between text-foreground">
            Orders:
          </div>

          <div className="no-scrollbar max-h-[21rem] overflow-y-auto">
            {scaleOrders.map((scaleOrder, index) => (
              <div
                key={`${scaleOrder.order.limitPrice}_${index}`}
                className="flex h-8 items-center justify-between"
              >
                <p className="flex items-center gap-2">#{index + 1}</p>

                <span className="font-mono leading-tight">
                  {formatNumber(scaleOrder.order.size, {
                    digits: Number(sizeDecimals),
                  })}{' '}
                  {baseSymbol} @{' '}
                  {formatNumber(scaleOrder.order.limitPrice, {
                    digits: Number(priceDecimals),
                  })}{' '}
                  {COLLATERAL_SYMBOL}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-2 flex h-10 items-center gap-3">
            <Checkbox
              id="order-confirm"
              className="mt-0.5 size-5"
              onCheckedChange={(checked) =>
                useSettingsStore.setState({ showScaleOrderConfirm: !checked })
              }
            />

            <Label
              htmlFor="order-confirm"
              className="cursor-pointer text-base leading-none text-vestgrey-600"
            >
              Don't show this again
            </Label>
          </div>
        </div>

        <DialogFooter className="grid pt-2 sm:justify-normal">
          <DialogClose asChild>
            <Button
              type="button"
              size="xl"
              className="w-full rounded-none uppercase tracking-wider"
              loading={isSubmitting}
              onClick={onSubmit}
            >
              Submit Order
            </Button>
          </DialogClose>

          <DialogClose asChild>
            <Button
              type="button"
              size="xl"
              variant="ghost"
              className="w-full rounded-none uppercase tracking-wider"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScaleOrderConfirm;
